import React, { useLayoutEffect } from 'react'
import { NavLink } from 'react-router-dom'
import { makeStyles } from '@material-ui/core/styles'
import { Box, Button, Link, Typography } from '@material-ui/core'
import { CheckCircleOutline } from '@material-ui/icons'

import { whatsappChatProps } from '../../../../components/whatsapp-chat'
import { styles } from './styles'
import { payLead, payDetail, chargesOnCompletion } from '../../../../lib/payment-copy'
import { PENDING_HEADING, PENDING_DETAIL, pendingLead, pendingChargeLine } from '../../../../lib/pay-by-link'
import { parse } from '../../../../lib/query'
import { clearBookingState } from '../../../../store/booking-persistence'

// Once per module: makeStyles called inside a render built a new JSS sheet,
// with new class names, on every render (booking screen, 11 Oct 2026).
const useStyles = makeStyles(styles)

export const StepFinish = () => {
  const classes = useStyles()

  useLayoutEffect(() => {
    window.scrollTo(0, 0)
    // Booked: the Book & pay draft has done its job (wallet path; the card
    // path clears it on /checkout/success).
    clearBookingState()
  }, [])

  // PAY_BY_LINK (1 Nov 2026): ?pending_payment=1 means a booking was taken
  // with no card and a link to add one has gone out, on WhatsApp by default
  // and by email as the backup (?via= says which). NO ADDRESS IN THE URL: it
  // would reach PostHog's $current_url, the Referer of any outbound link and
  // the server log (review finding, 2 Nov 2026).
  //
  // THE FORM SAID NOTHING ABOUT PAYMENT, on purpose. This surface is the
  // opposite: silence here would create a false impression rather than
  // withhold a frame, so it says plainly that we have their details and that
  // adding their card confirms the wash. It must NOT say "You're booked!" and
  // must not claim a confirmed slot. The charge sentence is arm A's, read off
  // CHARGE_ON_COMPLETION (pendingChargeLine), because the link takes no money
  // and this booking is charged at exactly the same point as any other.
  const query = typeof window === 'undefined' ? {} : parse(window.location.search)
  const pendingPayment = query.pending_payment === '1'
  const sentVia = typeof query.via === 'string' ? query.via : null

  if (pendingPayment) {
    return (
      <Box className={classes.container} data-testid="finish-pending-payment">
        <Typography className={classes.heading}>{PENDING_HEADING}</Typography>
        <Typography className={classes.subheading}>{pendingLead({ channel: sentVia })}</Typography>
        <Box className={classes.card}>
          <Typography className={classes.cardText}>{PENDING_DETAIL}</Typography>
        </Box>
        <Box className={classes.card}>
          <Typography className={classes.cardText}>{pendingChargeLine()}</Typography>
        </Box>
        <Box className={classes.card}>
          <Typography className={classes.cardTitle}>Link not arrived, or something wrong?</Typography>
          <Typography className={classes.cardText}>
            Message us on{' '}
            <Link {...whatsappChatProps('Hi GoWashMyCar! I have just asked for a wash 📱')} color="secondary" className={classes.link}>
              WhatsApp
            </Link>{' '}
            and we will sort it.
          </Typography>
        </Box>
        <Button
          component={NavLink}
          to="/"
          variant="contained"
          color="primary"
          className={classes.homeButton}
          disableElevation
        >
          Return to homepage
        </Button>
      </Box>
    )
  }

  return (
    <Box className={classes.container}>
      <Box className={classes.iconWrapper}>
        <CheckCircleOutline className={classes.icon} />
      </Box>
      <Typography className={classes.heading}>You’re booked!</Typography>
      <Typography className={classes.subheading}>We’ll message you on the day when your valeter sets off and again when they arrive.</Typography>
      <Box className={classes.card}>
        <Typography className={classes.cardText}>
          {chargesOnCompletion()
            ? `${payLead()} ${payDetail()}`
            : 'Your card is charged shortly after your booking is made. We keep it on file to make repeat bookings easy, and Stripe holds it, so we never see or store the details.'}
        </Typography>
      </Box>
      <Box className={classes.card}>
        <Typography className={classes.cardTitle}>Need to make changes?</Typography>
        <Typography className={classes.cardText}>
          Message us on{' '}
          <Link {...whatsappChatProps("Hi GoWashMyCar! I just placed an order 📱")} color="secondary" className={classes.link}>
            WhatsApp
          </Link>{' '}
          to amend your order.
        </Typography>
      </Box>
      <Box className={classes.socialRow}>
        <Typography className={classes.socialText}>Follow us on</Typography>
        <Link href="https://www.facebook.com/gowashmycar?ref=hl" target="_blank" rel="noopener noreferrer" color="secondary" className={classes.link}>
          Facebook
        </Link>
      </Box>
      <Button
        component={NavLink}
        to="/"
        variant="contained"
        color="primary"
        className={classes.homeButton}
        disableElevation
      >
        Return to homepage
      </Button>
    </Box>
  )
}
